import React from 'react';
import { MapPin, Mail, Phone, Instagram } from 'lucide-react';

export const Contact = () => {
  return (
    // Menggunakan font-body dan background konsisten
    <div className="min-h-screen pt-24 pb-16 font-body text-stone-700 bg-[var(--caramel)]/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold font-heading mb-4" style={{ color: 'var(--rufous)' }}>
            Get in Touch
          </h1>
          <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto">
            We welcome collaborations, custom orders, and cultural inquiries.
          </p>
          <div className="w-24 h-1 mx-auto mt-6" style={{ backgroundColor: 'var(--uc-gold)' }}></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold font-heading mb-6" style={{ color: 'var(--army-green)' }}>
                Contact Information
              </h2>

              {/* Location (New Card Style) */}
              <div className="mb-6 p-6 rounded-xl bg-white shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--caramel)' }}>
                    <MapPin className="w-6 h-6" style={{ color: 'var(--rufous)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold font-heading text-lg mb-2" style={{ color: 'var(--rufous)' }}>
                      Marajaki Kreatif Studio
                    </h3>
                    <p className="text-sm text-gray-700">
                      Central Kalimantan & East Java (Creative Hub)
                    </p>
                    <p className="text-sm text-gray-700 mt-1">
                      Jalan G.Obos No. 109 Sanggar Marajaki
                    </p>
                  </div>
                </div>
              </div>

              {/* Email (New Card Style) */}
              <div className="mb-6 p-6 rounded-xl bg-white shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--arylide-yellow)' }}>
                    <Mail className="w-6 h-6" style={{ color: 'var(--army-green)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold font-heading text-lg mb-2" style={{ color: 'var(--uc-gold)' }}>
                      Email
                    </h3>
                    <a
                      href="mailto:bennaurel@gmail.com"
                      className="text-sm text-gray-700 hover:text-[var(--rufous)] transition-colors"
                    >
                      bennaurel@gmail.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Phone (New Card Style) */}
              <div className="mb-6 p-6 rounded-xl bg-white shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--caramel)' }}>
                    <Phone className="w-6 h-6" style={{ color: 'var(--rufous)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold font-heading text-lg mb-2" style={{ color: 'var(--rufous)' }}>
                      WhatsApp
                    </h3>
                    <a
                      href="https://wa.me/6282154433065"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-700 hover:text-[var(--rufous)] transition-colors"
                    >
                      +62 821-5443-3065
                    </a>
                  </div>
                </div>
              </div>

              {/* Instagram (New Card Style) */}
              <div className="p-6 rounded-xl bg-white shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--arylide-yellow)' }}>
                    <Instagram className="w-6 h-6" style={{ color: 'var(--army-green)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold font-heading text-lg mb-2" style={{ color: 'var(--uc-gold)' }}>
                      Instagram Shop
                    </h3>
                    <a
                      href="https://www.instagram.com/marajaki.kreatif"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-700 hover:text-[var(--rufous)] transition-colors"
                    >
                      @marajaki.kreatif
                    </a>
                    <p className="text-xs text-gray-500 mt-1">
                      See new drops, behind-the-scenes weaving, and daily updates.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Visual/Info Section */}
          <div className="space-y-8">
            <div className="p-8 rounded-xl" style={{ backgroundColor: 'var(--caramel)' }}>
              <h3 className="text-xl font-bold font-heading mb-4" style={{ color: 'var(--army-green)' }}>
                Let's Collaborate
              </h3>
              <p className="text-sm text-stone-800 mb-6">
                Whether you're interested in custom orders, cultural collaborations, wholesale inquiries, or simply want to learn more about Sapuyung Dare heritage—we'd love to hear from you.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--rufous)' }}></div>
                  <span className="text-sm text-stone-800">Custom product designs</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--rufous)' }}></div>
                  <span className="text-sm text-stone-800">Wholesale partnerships</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--rufous)' }}></div>
                  <span className="text-sm text-stone-800">Cultural exhibitions</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--rufous)' }}></div>
                  <span className="text-sm text-stone-800">Media & press inquiries</span>
                </div>
              </div>
            </div>
            {/* Anda bisa menambahkan gambar kembali di sini jika mau */}
            {/* <div className="rounded-xl overflow-hidden shadow-lg">
                <img src="https..."/>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};